import React, {useEffect, useRef} from 'react'
import L from 'leaflet'
import MapCanvas from '../../modal-global/map-dialog/map-canvas'
import {buildRouteSegments} from './buildRouteSegments'

const LEGEND_ITEMS = [
    {color: '#3b82f6', dash: null, label: 'On load'},
    {color: '#22c55e', dash: '8 5', label: 'Empty miles'},
    {color: '#ef4444', dash: '8 5', label: 'Empty miles (stale)'},
    {color: '#9ca3af', dash: '4 4', label: 'Manual connection'},
]

function getStopIcon(marker) {
    const color = marker.Source === 'Manual' ? '#8b5cf6' : '#3b82f6'
    return L.divIcon({
        className: '',
        html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3)"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
    })
}

function getStopPopup(marker) {
    const locationLabel = [marker.LocationName, marker.AddressName].filter(Boolean).join(' - ')
    const cityState = [marker.CityName, marker.State].filter(Boolean).join(', ')
    const isManual = marker.Source === 'Manual'
    const date = marker.LocationDate
        ? new Date(marker.LocationDate).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        })
        : null

    return (
        <div style={{minWidth: '180px', fontFamily: 'sans-serif'}}>
            {locationLabel && (
                <div style={{fontWeight: '700', fontSize: '13px', marginBottom: '4px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px'}}>
                    {locationLabel}
                </div>
            )}
            {cityState && (
                <div style={{fontSize: '12px', color: '#374151', marginBottom: '2px'}}>{cityState}</div>
            )}
            {date && (
                <div style={{fontSize: '11px', color: '#6b7280', marginBottom: '8px'}}>{date}</div>
            )}

            <div style={{marginBottom: '6px'}}>
                {!isManual && marker.LoadNumber && (
                    <span style={{background: '#eff6ff', color: '#1d4ed8', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: '600', marginRight: '4px'}}>
                        Load #{marker.LoadNumber}
                    </span>
                )}
                <span style={{background: isManual ? '#f5f3ff' : '#f0fdf4', color: isManual ? '#7c3aed' : '#15803d', borderRadius: '3px', padding: '1px 6px', fontSize: '10px'}}>
                    {marker.Source}
                </span>
            </div>

            {!isManual && (marker.TotalMiles || marker.EmptyMiles) && (
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '6px'}}>
                    <div style={{background: '#f0fdf4', borderRadius: '4px', padding: '4px 6px'}}>
                        <div style={{color: '#9ca3af', fontSize: '9px'}}>On Load</div>
                        <div style={{fontWeight: '700', color: '#15803d', fontSize: '12px'}}>
                            {marker.TotalMiles ? `${parseFloat(marker.TotalMiles).toFixed(1)} mi` : '—'}
                        </div>
                    </div>
                    <div style={{background: '#fff7ed', borderRadius: '4px', padding: '4px 6px'}}>
                        <div style={{color: '#9ca3af', fontSize: '9px'}}>Empty</div>
                        <div style={{fontWeight: '700', color: '#c2410c', fontSize: '12px'}}>
                            {marker.EmptyMiles ? `${parseFloat(marker.EmptyMiles).toFixed(1)} mi` : '—'}
                        </div>
                    </div>
                </div>
            )}

            {marker.Notes && (
                <div style={{fontSize: '11px', color: '#6b7280', fontStyle: 'italic'}}>{marker.Notes}</div>
            )}
        </div>
    )
}

export default function RouteMapView({locations}) {
    const fitBoundsRef = useRef(null)

    // Auto-fit bounds on mount and whenever locations change (e.g. filter/reload while map is active).
    useEffect(() => {
        const timer = setTimeout(() => {
            if (fitBoundsRef.current) fitBoundsRef.current()
        }, 300)
        return () => clearTimeout(timer)
    }, [locations])

    const validLocations = (locations || []).filter(loc => loc.Latitude != null && loc.Longitude != null)
    const segments = buildRouteSegments(locations || [])

    if (validLocations.length === 0) {
        return (
            <div style={{height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontFamily: 'sans-serif'}}>
                No locations with coordinates to display.
            </div>
        )
    }

    return (
        <div style={{position: 'relative', height: '500px'}}>
            <MapCanvas
                data={validLocations}
                areMarkerClustered={false}
                fitBoundsRef={fitBoundsRef}
                routeSegments={segments}
                onGetCustomIcon={getStopIcon}
                onGetPopupContent={getStopPopup}
            />
            <div style={{
                position: 'absolute', bottom: '24px', left: '10px', zIndex: 1000,
                background: 'white', borderRadius: '6px', padding: '8px 12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: '11px',
                fontFamily: 'sans-serif', pointerEvents: 'none',
            }}>
                {LEGEND_ITEMS.map(item => (
                    <div key={item.label} style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px'}}>
                        <svg width="24" height="10" style={{flexShrink: 0}}>
                            <line x1="0" y1="5" x2="24" y2="5" stroke={item.color} strokeWidth="2" strokeDasharray={item.dash || ''}/>
                        </svg>
                        <span style={{color: '#374151', whiteSpace: 'nowrap'}}>{item.label}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}
